import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ErrorBanner, Spinner } from '@platform/ui';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import type { IMenuEntry } from '@platform/engine/menu/IMenuEntry';
import { designColumnShown } from './designColumns';
import { DesignListBody } from './DesignListBody';
import { analysisModuleKey, designFilterOf, designPresetOf, designSubOf, designSubsOf, kindField } from './designSections';
import { DesignSubPills } from './DesignSubPills';
import type { IDesignListSectionProps } from './IDesignListSectionProps';
import { useAnalysisModule } from './useAnalysisModule';
import { useJourneyCodes } from './useJourneyCodes';
import './design.css';

/** Параметр адреси з активною вкладкою розділу (посилання на вкладку відкриває її). */
const SUB = 'sub';

/**
 * Розділ «Проєктування» зі списком записів бізнес-аналізу модуля (прототип v4 dsgGo): вкладки розділу з лічильниками (вибрана — у `?sub=`),
 * фільтр вкладки за видом запису й модулем (назва модуля англійською, як поле module записів аналізу; наскрізні шляхи — номери шляхів, що
 * проходять через модуль, useJourneyCodes), список модуля бізнес-аналізу з усіма поданнями без колонок модуля, виду й полів інших видів
 * (designColumnShown) і створенням запису з наперед заданими видом і модулем (кілька видів — меню «+ Створити ▾»).
 */
export function DesignListSection({ ws, host, section, crumbs, header }: IDesignListSectionProps) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const analysis = useAnalysisModule();
  const strings = useModuleStrings(analysisModuleKey);
  const journeys = useJourneyCodes(ws, section === 'journeys' ? host : undefined);
  const sub = designSubOf(section, params.get(SUB));
  const moduleName = host.module.name.en ?? host.module.key;
  const subs = designSubsOf(section).map((s) => ({ sub: s, label: t(`modDesign.subs.${section}.${s.key}`), filter: designFilterOf(s, moduleName, journeys) }));
  const filter = sub ? designFilterOf(sub, moduleName, journeys) : '';
  const entry = useMemo((): IMenuEntry => ({ key: `design:${host.module.key}:${section}:${sub?.key ?? ''}`, label: '', group: '', view: 'table', filter }),
    [host.module.key, section, sub?.key, filter]);
  const showColumn = useCallback((field: string) => !sub || designColumnShown(section, sub, field), [section, sub]);
  if (analysis.loading || (section === 'journeys' && !journeys)) return <Spinner />;
  if (!analysis.module || !analysis.rt || !sub) return <ErrorBanner error={analysis.error ?? { message: t('modDesign.noAnalysis') }} />;
  const rtKey = analysis.rt.key;
  const options = analysis.rt.fields.find((f) => f.key === kindField)?.options ?? [];
  const kinds = sub.kinds.length > 1 ? sub.kinds.map((k) => ({ value: k, label: strings.option(rtKey, kindField, k, options.find((o) => o.value === k)?.label) })) : [];
  const select = (key: string) => setParams((p) => { const next = new URLSearchParams(p); next.set(SUB, key); return next; }, { replace: true });
  const title = (
    <div className="dsg-head">
      {header}
      {subs.length > 1 && <DesignSubPills ws={ws} rt={rtKey} subs={subs} active={sub.key} onSelect={select} />}
    </div>
  );
  return (
    <DesignListBody ws={ws} module={analysis.module} rt={analysis.rt} entry={entry} preset={designPresetOf(sub, moduleName)}
      createText={t(`modDesign.create.${section}.${sub.key}`)} crumbs={crumbs} title={title} showColumn={showColumn} kinds={kinds} />
  );
}
